import * as React from 'react';
import { Portal } from '@radix-ui/react-portal';
import { Presence } from '@radix-ui/react-presence';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { DismissableLayer } from '@radix-ui/react-dismissable-layer';

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Classes for the content panel — position, size, and the `data-[state]` enter/exit animation. */
  className?: string;
  /** Classes for the scrim/overlay — usually `fixed inset-0 bg-black/50` + a `data-[state]` fade. */
  overlayClassName?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/**
 * Sticky-safe modal primitive.
 *
 * Composes Radix's FocusScope (focus trap), DismissableLayer (Esc / click-outside), Presence
 * (enter/exit animations via `data-[state]` classes) and Portal — i.e. everything Radix's modal
 * Dialog gives — EXCEPT `react-remove-scroll`. Radix's modal Dialog locks background scroll by
 * setting `overflow: hidden` on `<body>`, which turns body into a scroll container and breaks the
 * app's `position: sticky` header/rail (see the `overflow-x: clip` note in index.css). Instead we
 * lock scroll with a wheel/touchmove event-swallow that never touches the body — so sticky keeps
 * working. Inside the panel, the consumer's own `overflow-y-auto` + `overscroll-contain` scrolls
 * normally.
 */
export function Modal({
  open,
  onOpenChange,
  children,
  className,
  overlayClassName,
  ...aria
}: ModalProps) {
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Lock background scroll without mutating <body> (which would break sticky). A gesture is allowed
  // only when a scrollable element *inside the panel* can actually consume it in that direction —
  // otherwise it's swallowed, so it can never chain out to the page (a wheel over the panel's
  // non-scrolling chrome, or past a scroll region's edge, is blocked).
  React.useEffect(() => {
    if (!open) return;
    const canConsume = (target: EventTarget | null, deltaY: number): boolean => {
      const panel = panelRef.current;
      if (!panel || !(target instanceof Node) || !panel.contains(target)) return false;
      let el: Element | null = target instanceof Element ? target : target.parentElement;
      while (el && panel.contains(el)) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
          if (deltaY < 0) return el.scrollTop > 0;                                   // scrolling up
          if (deltaY > 0) return el.scrollTop + el.clientHeight < el.scrollHeight - 1; // scrolling down
          return true; // touch (deltaY 0) — overscroll-contain handles the edges
        }
        el = el.parentElement;
      }
      return false;
    };
    const onWheel = (e: WheelEvent) => { if (!canConsume(e.target, e.deltaY)) e.preventDefault(); };
    const onTouchMove = (e: TouchEvent) => { if (!canConsume(e.target, 0)) e.preventDefault(); };
    document.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      document.removeEventListener('wheel', onWheel);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [open]);

  const state = open ? 'open' : 'closed';

  return (
    <Portal>
      <Presence present={open}>
        <div data-state={state} className={overlayClassName} aria-hidden />
      </Presence>
      <Presence present={open}>
        <FocusScope asChild loop trapped>
          <DismissableLayer
            asChild
            onEscapeKeyDown={() => onOpenChange(false)}
            onPointerDownOutside={() => onOpenChange(false)}
            onFocusOutside={(e) => e.preventDefault()}
          >
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              data-state={state}
              className={className}
              {...aria}
            >
              {children}
            </div>
          </DismissableLayer>
        </FocusScope>
      </Presence>
    </Portal>
  );
}

export default Modal;
